import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Save, FolderOpen, Trash2, X, FileSearch } from "lucide-react";
import { API, authHeaders, formatApiErrorDetail } from "./api";
import { Panel, Field, Input, Select } from "../pages/calculator/parts";

const fmtWhen = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export const ScenarioBar = ({ state, setState, loaded, setLoaded, customers, fixedCustomerId = null }) => {
  const [customerId, setCustomerId] = useState(fixedCustomerId || "");
  const [name, setName] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [listOpen, setListOpen] = useState(false);
  const [scenarios, setScenarios] = useState([]);
  const [listCustomerId, setListCustomerId] = useState(fixedCustomerId || "");
  const [listLoading, setListLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  // Keep the customer picker locked to the gate-selected customer. Loading a
  // scenario can only change the active customer via the Calculator's own
  // effect; the local state follows along.
  useEffect(() => {
    if (fixedCustomerId && !loaded) {
      setCustomerId(fixedCustomerId);
      setListCustomerId(fixedCustomerId);
    }
  }, [fixedCustomerId, loaded]);

  // Pre-populate the "name" + "customer" fields when a scenario is already
  // loaded so the Update button writes back to the same record.
  useEffect(() => {
    if (loaded) {
      setCustomerId(loaded.customer_id || "");
      setName(loaded.name || "");
      setNotes(loaded.notes || "");
    }
  }, [loaded]);

  // Default the "list" dropdown to the current customer if the user picked one.
  useEffect(() => {
    if (customerId && !listCustomerId) setListCustomerId(customerId);
  }, [customerId, listCustomerId]);

  const loadList = async (cid) => {
    if (!cid) {
      setScenarios([]);
      return;
    }
    setListLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/admin/customers/${cid}/scenarios`,
        authHeaders()
      );
      setScenarios(data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load scenarios");
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    if (listOpen) loadList(listCustomerId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listOpen, listCustomerId]);

  const saveNew = async () => {
    if (!customerId) {
      toast.error("Pick a customer to save this scenario against.");
      return;
    }
    if (!name.trim()) {
      toast.error("Give this scenario a short name (e.g. 'Primary residence £400k').");
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/customers/${customerId}/scenarios`,
        { name: name.trim(), notes: notes.trim(), data: state },
        authHeaders()
      );
      setLoaded(data);
      toast.success(`Saved against ${data.customer_name}.`);
      if (listOpen && listCustomerId === customerId) loadList(customerId);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save scenario");
    } finally {
      setSaving(false);
    }
  };

  const updateExisting = async () => {
    if (!loaded) return;
    setSaving(true);
    try {
      const { data } = await axios.put(
        `${API}/admin/scenarios/${loaded.id}`,
        { name: name.trim(), notes: notes.trim(), data: state },
        authHeaders()
      );
      setLoaded(data);
      toast.success("Scenario updated.");
      if (listOpen && listCustomerId === loaded.customer_id) loadList(loaded.customer_id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update");
    } finally {
      setSaving(false);
    }
  };

  const loadScenario = (s) => {
    if (!s?.data) return;
    setState((prev) => ({ ...prev, ...s.data, payeManual: true }));
    setLoaded(s);
    setListOpen(false);
    toast.success(`Loaded "${s.name}".`);
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 60);
  };

  const removeScenario = async () => {
    const target = confirmDelete;
    if (!target) return;
    try {
      await axios.delete(`${API}/admin/scenarios/${target.id}`, authHeaders());
      toast.success("Scenario removed.");
      setConfirmDelete(null);
      if (loaded?.id === target.id) setLoaded(null);
      loadList(listCustomerId);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not delete");
    }
  };

  const unlink = () => {
    setLoaded(null);
    setName("");
    setNotes("");
    setCustomerId("");
    toast("Working on a fresh scenario.");
  };

  return (
    <Panel title="Save scenario" testid="calc-scenario-bar">
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.3fr_auto] gap-4 items-end">
        <Field label="Customer">
          <Select
            value={customerId}
            onChange={(e) => setCustomerId(e.target.value)}
            disabled={!!loaded || !!fixedCustomerId}
            testid="calc-scenario-customer"
          >
            <option value="">Pick a customer</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
                {c.company ? ` — ${c.company}` : ""}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Scenario name">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Primary residence £400k"
            testid="calc-scenario-name"
          />
        </Field>
        <div className="flex flex-wrap gap-2">
          {loaded ? (
            <button
              type="button"
              onClick={updateExisting}
              disabled={saving}
              data-testid="calc-scenario-update"
              className="inline-flex items-center gap-2 bg-forest hover:bg-forest-dark text-cream px-5 py-3 text-[11px] tracking-[0.2em] uppercase font-bold transition-colors disabled:opacity-50"
            >
              <Save size={14} />
              {saving ? "Saving…" : "Update"}
            </button>
          ) : (
            <button
              type="button"
              onClick={saveNew}
              disabled={saving}
              data-testid="calc-scenario-save"
              className="inline-flex items-center gap-2 bg-forest hover:bg-forest-dark text-cream px-5 py-3 text-[11px] tracking-[0.2em] uppercase font-bold transition-colors disabled:opacity-50"
            >
              <Save size={14} />
              {saving ? "Saving…" : "Save"}
            </button>
          )}
          <button
            type="button"
            onClick={() => setListOpen((v) => !v)}
            data-testid="calc-scenario-browse"
            className="inline-flex items-center gap-2 border border-forest/25 hover:border-forest text-forest px-5 py-3 text-[11px] tracking-[0.2em] uppercase font-bold transition-colors"
          >
            <FolderOpen size={14} />
            {listOpen ? "Hide saved" : "Saved scenarios"}
          </button>
        </div>
      </div>
      <Field label="Internal note (optional)">
        <Input
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="e.g. For June call, prefer 25-year term"
          testid="calc-scenario-notes"
        />
      </Field>

      {loaded && (
        <div
          data-testid="calc-scenario-loaded"
          className="flex items-center gap-3 bg-cream-3 border border-forest/15 px-4 py-3"
        >
          <FileSearch size={16} className="text-forest shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-[12.5px] text-forest font-semibold truncate">
              Editing {loaded.name}
              <span className="text-sage font-normal"> · {loaded.customer_name}</span>
            </p>
            <p className="text-[11px] text-sage">
              Last saved {fmtWhen(loaded.updated_at)} by {loaded.created_by}
            </p>
          </div>
          <button
            type="button"
            onClick={unlink}
            data-testid="calc-scenario-unlink"
            title="Start a fresh scenario"
            className="p-2 text-forest/60 hover:text-forest transition-colors shrink-0"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {listOpen && (
        <div className="pt-4 border-t border-forest/10 space-y-3" data-testid="calc-scenario-list">
          <Field label="Filter by customer">
            <Select
              value={listCustomerId}
              onChange={(e) => setListCustomerId(e.target.value)}
            >
              <option value="">All customers (pick one to view)</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </Field>
          {listLoading ? (
            <p className="text-sm text-sage">Loading…</p>
          ) : !listCustomerId ? (
            <p className="text-sm text-sage">Pick a customer above to see their saved scenarios.</p>
          ) : scenarios.length === 0 ? (
            <p className="text-sm text-sage">No scenarios saved for this customer yet.</p>
          ) : (
            <ul className="divide-y divide-forest/10">
              {scenarios.map((s) => (
                <li
                  key={s.id}
                  className="py-3 flex items-start sm:items-center justify-between gap-3 flex-wrap"
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-serif text-[16px] text-forest truncate">{s.name}</p>
                    <p className="text-[11.5px] text-sage">
                      Last saved {fmtWhen(s.updated_at)}
                      {s.notes ? ` · ${s.notes}` : ""}
                    </p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => loadScenario(s)}
                      data-testid={`calc-scenario-load-${s.id}`}
                      className="text-[11px] font-bold tracking-[0.14em] uppercase text-forest border border-forest/25 hover:border-forest px-3.5 py-2 transition-colors"
                    >
                      Load
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmDelete(s)}
                      data-testid={`calc-scenario-delete-${s.id}`}
                      title="Remove scenario"
                      className="p-2 text-forest/60 hover:text-red-600 transition-colors"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {confirmDelete && (
        <div
          data-testid="calc-scenario-delete-modal"
          className="fixed inset-0 z-50 bg-forest/50 backdrop-blur-sm flex items-center justify-center px-4"
          onClick={() => setConfirmDelete(null)}
        >
          <div
            className="bg-cream max-w-md w-full p-7 border border-forest/15 shadow-[0_30px_80px_rgba(0,0,0,0.3)]"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-serif text-2xl text-forest mb-2">Remove scenario?</h3>
            <p className="text-[13.5px] text-ink/80 leading-relaxed mb-5">
              This will hide <strong className="text-forest">{confirmDelete.name}</strong> from the saved list for{" "}
              <strong>{confirmDelete.customer_name}</strong>. The record is kept in your database and can be restored if needed.
            </p>
            <div className="flex gap-3 flex-wrap">
              <button
                type="button"
                onClick={removeScenario}
                data-testid="calc-scenario-delete-confirm"
                className="bg-red-700 hover:bg-red-800 text-cream px-6 py-3 text-[11px] tracking-[0.2em] uppercase font-bold transition-colors"
              >
                Yes, remove it
              </button>
              <button
                type="button"
                onClick={() => setConfirmDelete(null)}
                className="btn-ghost-dark"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </Panel>
  );
};
